import { UserPlus, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/**
 * Public homepage real-time stats — Step 1 hotfix.
 *
 * STRICT NO-FINANCIAL-FIGURE POLICY. Only two aggregate sign-up counters
 * are rendered: "Sign-ups Today" (rolling 24h) and "Total Sign-ups".
 *
 * Uses plain fetch (no auth-aware axios client) so anonymous visitors never
 * leak an X-User-Id header. Polled every 15 s; the backend caches the
 * response for 10 s in-memory so bot polling cannot translate to DB load.
 */
function useCountUp(target, { duration = 900 } = {}) {
  const [value, setValue] = useState(target || 0);
  const startTs = useRef(null);
  const fromRef = useRef(target || 0);
  const raf = useRef(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `value` is only the tween start point, read when target/duration change; depending on it would restart the tween every frame
  useEffect(() => {
    cancelAnimationFrame(raf.current);
    const from = value;
    fromRef.current = from;
    startTs.current = null;

    if (from === target) return undefined;

    const ease = (t) => 1 - (1 - t) ** 3;
    const tick = (ts) => {
      if (!startTs.current) startTs.current = ts;
      const p = Math.min(1, (ts - startTs.current) / duration);
      setValue(Math.round(from + (target - from) * ease(p)));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return value;
}

const fmt = (n) => Number(n ?? 0).toLocaleString();

const StatCard = ({ icon: Icon, label, value, sublabel, accent, testid, pulse }) => {
  const animated = useCountUp(Number(value ?? 0));
  return (
    <div className="sc-glass rounded-2xl p-6 md:p-7 relative overflow-hidden group" data-testid={testid}>
      <div
        className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none transition-transform duration-700 group-hover:scale-110"
        style={{
          background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
        }}
      />
      <div className="relative z-10 flex items-start justify-between">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center border"
          style={{ background: 'rgba(255, 182, 18, 0.06)', borderColor: 'rgba(255, 182, 18, 0.28)' }}
        >
          <Icon className="w-[18px] h-[18px]" style={{ color: '#FFC20E' }} />
        </div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] flex items-center gap-1.5">
          {pulse ? <span className="w-1.5 h-1.5 rounded-full bg-[#FFB612] animate-pulse" aria-hidden="true" /> : null}
          {accent}
        </span>
      </div>

      <div className="relative z-10 mt-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{label}</div>
        <div className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-2 leading-none sc-gold-text" data-testid={`${testid}-value`}>
          {fmt(animated)}
        </div>
        <div className="mt-2 text-xs text-[var(--sc-text-dim)] leading-snug">{sublabel}</div>
      </div>
    </div>
  );
};

const SignupStats = () => {
  const [stats, setStats] = useState({ signups_today: 0, total_signups: 0 });

  useEffect(() => {
    const backendUrl = (typeof process !== 'undefined' && process.env.REACT_APP_BACKEND_URL) || '';
    const url = `${backendUrl}/api/public/signup-stats`;
    let alive = true;

    const load = async () => {
      try {
        const r = await fetch(url, {
          method: 'GET',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!alive) return;
        if (data && typeof data === 'object') {
          setStats({
            signups_today: Number(data.signups_today ?? 0),
            total_signups: Number(data.total_signups ?? 0),
          });
        }
      } catch {
        /* swallow — retry on next tick */
      }
    };

    load();
    const id = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  return (
    <section className="relative max-w-7xl mx-auto px-5 md:px-8 pb-10 md:pb-14 sc-fade-in" style={{ animationDelay: '0.4s' }} data-testid="signup-stats">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <StatCard
          testid="stat-signups-today"
          icon={UserPlus}
          label="Sign-ups Today"
          value={stats.signups_today}
          sublabel="New members joined in the last 24 hours."
          accent="Live · Rolling 24h"
          pulse
        />
        <StatCard
          testid="stat-signups-total"
          icon={Users}
          label="Total Sign-ups"
          value={stats.total_signups}
          sublabel="Cumulative members on 1SimpleCycler 3.0."
          accent="All-time"
        />
      </div>
    </section>
  );
};

export default SignupStats;
